/**
 * QuickActionsWidget — karty skrótów (Figma „Zaproś użytkownika"): kafelek z ikoną, tytuł,
 * krótki opis i strzałka; stany hover i focus-visible. Bez backendu — akcje definiuje config
 * kafelka (etykieta → ścieżka react-router). `onMouseDown` stop-propagation, żeby klik w link
 * nie startował przeciągania w trybie edycji siatki.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import type { WidgetRenderProps } from '../types';
import { IconTile } from '../widget-ui';

import { WidgetEmpty } from './WidgetState';

export function QuickActionsWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  if (widget.type !== 'quick_actions') return null;

  const actions = widget.config.actions ?? [];
  if (actions.length === 0)
    return <WidgetEmpty message={t('tenant.dashboard.widgets.quick_actions.empty')} />;

  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
      {actions.map((action, idx) => (
        <Link
          key={`${action.to}-${idx}`}
          to={action.to}
          onMouseDown={(e) => e.stopPropagation()}
          className="group flex items-center gap-3 rounded-xl border border-border-secondary bg-bg-elevated px-3 py-3 transition-colors hover:border-accent-500 hover:bg-bg-secondary focus-visible:border-accent-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40"
        >
          <IconTile icon={action.icon ?? 'Zap'} tone="accent" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-text-primary">
              {action.label}
            </span>
            {action.description && (
              <span className="line-clamp-2 text-xs text-text-tertiary">{action.description}</span>
            )}
          </span>
          <ChevronRight
            className="h-4 w-4 shrink-0 text-text-muted transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      ))}
    </div>
  );
}
